import classNames from "classnames";
import React from "react";
import { Link } from "react-router-dom";
import Hint, { Explanation, InfoDot } from "./Hint";
import { Health, ScheduleStatus, Stage } from "../types";

export type Tone = "neutral" | "trading" | "design" | "fitout" | "vacant" | "refurb" | "dark";

interface StatCircleProps {
	value: React.ReactNode;
	caption: React.ReactNode;
	label: string;
	tone: Tone;
	size?: "md" | "lg";
	explain: Explanation;
	sublabel?: string;
	to?: string;
}

export const StatCircle = ({ value, caption, label, tone, size = "md", explain, sublabel, to }: StatCircleProps) => (
	<Target to={to} explain={explain} className="ed-stat">
		<div className="ed-stat__slot">
			<div className={classNames("ed-stat__circle", `ed-tone--${tone}`, `ed-stat__circle--${size}`)}>
				<span className="ed-stat__value">{value}</span>
				<span className="ed-stat__caption">{caption}</span>
			</div>
		</div>
		<div className={classNames("ed-stat__label", { "ed-stat__label--strong": size === "lg" })}>{label}</div>
		{sublabel && <div className="ed-stat__sublabel">{sublabel}</div>}
	</Target>
);

interface TargetProps {
	to?: string;
	explain: Explanation;
	className: string;
	children: React.ReactNode;
}

const Target = ({ to, explain, className, children }: TargetProps) => (
	<Hint explain={explain} className={className} block>
		{to ? (
			<Link to={to} className="ed-target">
				{children}
			</Link>
		) : (
			children
		)}
	</Hint>
);

interface RingProps {
	fraction: number;
	label: string;
	amount: string;
	secondary?: string;
	caption: string;
	tone: Tone;
	explain: Explanation;
	to?: string;
}

export const Ring = ({ fraction, label, amount, secondary, caption, tone, explain, to }: RingProps) => {
	const radius = 52;
	const circumference = 2 * Math.PI * radius;
	const clamped = Math.max(0, Math.min(1, fraction));
	return (
		<Target to={to} explain={explain} className="ed-ring">
			<svg className={classNames("ed-ring__svg", `ed-tone--${tone}`)} viewBox="0 0 128 128" aria-hidden="true">
				<circle className="ed-ring__track" cx="64" cy="64" r={radius} />
				<circle
					className="ed-ring__arc"
					cx="64"
					cy="64"
					r={radius}
					strokeDasharray={`${circumference * clamped} ${circumference}`}
					transform="rotate(-90 64 64)"
				/>
				<text x="64" y="70" textAnchor="middle" className="ed-ring__label">
					{label}
				</text>
			</svg>
			<div className="ed-ring__amount">{amount}</div>
			{secondary && <div className="ed-ring__secondary">{secondary}</div>}
			<div className="ed-ring__caption">{caption}</div>
		</Target>
	);
};

interface KpiProps {
	label: string;
	value: React.ReactNode;
	sub?: React.ReactNode;
	dot?: Tone;
	valueTone?: Tone;
	explain: Explanation;
}

export const Kpi = ({ label, value, sub, dot, valueTone, explain }: KpiProps) => (
	<Hint explain={explain} className="ed-kpi" block>
		<div className="ed-kpi__label">
			{dot && <span className={classNames("ed-dot", `ed-tone--${dot}`)} />}
			{label}
		</div>
		<div className={classNames("ed-kpi__value", valueTone && `ed-text--${valueTone}`)}>{value}</div>
		{sub && <div className="ed-kpi__sub">{sub}</div>}
	</Hint>
);

interface CardProps {
	eyebrow?: string;
	title?: React.ReactNode;
	subtitle?: React.ReactNode;
	tag?: string;
	explain?: Explanation;
	actions?: React.ReactNode;
	children: React.ReactNode;
	className?: string;
}

export const Card = ({ eyebrow, title, subtitle, tag, explain, actions, children, className }: CardProps) => (
	<section className={classNames("ed-card", className)}>
		{(eyebrow || title || tag || actions) && (
			<header className="ed-card__header">
				<div>
					{eyebrow && (
						<div className="ed-eyebrow">
							{eyebrow}
							{explain && <InfoDot explain={explain} align="left" />}
						</div>
					)}
					{title && <h2 className="ed-card__title">{title}</h2>}
					{subtitle && <div className="ed-card__subtitle">{subtitle}</div>}
				</div>
				<div className="ed-card__aside">
					{tag && <span className="ed-tag">{tag}</span>}
					{actions}
				</div>
			</header>
		)}
		{children}
	</section>
);

const HEALTH_LABELS: { [key in Health]: string } = {
	on_track: "On track",
	at_risk: "At risk",
	delayed: "Delayed"
};

export const HealthBadge = ({ health, days, explain }: { health: Health; days?: number | null; explain: Explanation }) => (
	<Hint explain={explain} align="right">
		<span className={classNames("ed-badge", `ed-badge--${health}`)}>
			<span className="ed-badge__dot" />
			{HEALTH_LABELS[health]}
			{health !== "on_track" && days ? ` · ${days} d` : ""}
		</span>
	</Hint>
);

export const CountBadge = ({ tone, children }: { tone: "on_track" | "at_risk" | "delayed" | "neutral"; children: React.ReactNode }) => (
	<span className={classNames("ed-badge", `ed-badge--${tone}`)}>
		<span className="ed-badge__dot" />
		{children}
	</span>
);

export const StagePill = ({ stage, label }: { stage: Stage | ScheduleStatus; label: string }) => (
	<span className={classNames("ed-pill", `ed-pill--${stage}`)}>
		<span className="ed-pill__dot" />
		{label}
	</span>
);

const PENDING_PROJECT = "The project is pending: it was created from Yardi but hasn't been scheduled yet, so its workflow hasn't started.";

export const PendingTag = () => (
	<Hint explain={{ title: "Pending project", lines: [PENDING_PROJECT] }}>
		<span className="ed-pending">Pending</span>
	</Hint>
);

export const ProgressBar = ({ value, tone }: { value: number; tone: Health }) => (
	<span className="ed-progress">
		<span className="ed-progress__track">
			<span className={classNames("ed-progress__bar", `ed-progress__bar--${tone}`)} style={{ width: `${value}%` }} />
		</span>
		<span className="ed-progress__value">{value}%</span>
	</span>
);

export const Avatar = ({ initials, name, extra }: { initials: string; name: string; extra?: number }) => {
	const palette = ["#2f6fdf", "#1f9d74", "#d9534f", "#8b5cf6", "#e08a1e", "#0e8fa0"];
	const index = name.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0) % palette.length;
	const short = name.split(" ").length > 1 ? `${name.split(" ")[0]} ${name.split(" ").slice(-1)[0][0]}.` : name;
	return (
		<span className="ed-avatar">
			<span className="ed-avatar__circle" style={{ backgroundColor: palette[index] }}>
				{initials}
			</span>
			<span className="ed-avatar__name">
				{short}
				{extra ? <span className="ed-avatar__extra"> +{extra}</span> : null}
			</span>
		</span>
	);
};

export const Loading = () => (
	<div className="ed-state">
		<span className="ed-spinner" />
		Loading dashboard data
	</div>
);

export const ErrorState = () => <div className="ed-state ed-state--error">The dashboard data could not be loaded.</div>;

export const Empty = ({ children }: { children: React.ReactNode }) => <div className="ed-empty">{children}</div>;
